import axios, { type AxiosError } from 'axios';
import { toast } from '@/hooks/use-toast';
import { getCurrentTenantId, getTenantHeaders } from '@/lib/tenant';
import { API_URL } from '@/lib/api-url';

// Create an Axios instance with default config
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds
});

// Helper function to extract error message from complex error response
export function extractErrorMessage(errorData: any): string {
  try {
    // Check for the specific structure you're receiving
    if (errorData?.message?.message) {
      return errorData.message.message;
    }

    // Check for direct message
    if (errorData?.message && typeof errorData.message === 'string') {
      return errorData.message;
    }

    // Check for error array (validation errors)
    if (Array.isArray(errorData?.message)) {
      return errorData.message[0];
    }

    // Check for axios error response
    if (errorData?.response?.data) {
      return extractErrorMessage(errorData.response.data);
    }

    // Check if it's an Error object
    if (errorData instanceof Error) {
      return errorData.message;
    }

    // Fallback to generic message
    return 'An unexpected error occurred';
  } catch (e) {
    console.error('Error while extracting error message:', e);
    return 'An unexpected error occurred';
  }
}

// Function to show toast with deduplication
function showErrorToast(message: string) {
  // Only run on client side
  if (typeof window === 'undefined') return;

  // Use setTimeout to ensure the toast is shown after the current execution context
  setTimeout(() => {
    toast({
      title: 'Error',
      description: message,
      variant: 'destructive',
    });
  }, 0);
}

// Add request interceptor for debugging
apiClient.interceptors.request.use(
  (config) => {
    console.log(
      `API Request: ${config.method?.toUpperCase()} ${config.baseURL}${
        config.url
      }`,
      config.data || ''
    );

    // Only run on client side
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('auth_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add tenant header for multi-tenancy support
      const tenantId = getCurrentTenantId();
      if (tenantId && config.headers) {
        config.headers['X-Tenant-ID'] = tenantId;
      }
    }
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    console.error(
      'Response Error:',
      error.response?.status,
      error.response?.data,
      error.config?.url
    );

    // Silently ignore 404s — callers handle "not found" as an empty/null state
    if (error.response?.status === 404) {
      return Promise.reject(error);
    }

    const errorMessage = extractErrorMessage(error.response?.data || error);
    showErrorToast(errorMessage);

    return Promise.reject(error);
  }
);

interface ApiRequestOptions {
  method?: string;
  url: string;
  data?: any;
  headers?: HeadersInit;
  suppressToast?: boolean; // Option to suppress toast if needed
  config?: {
    skipJsonParsing?: boolean;
  };
}

// Type-safe API request function
export async function apiRequest<T = any>({
  method = 'GET',
  url,
  data,
  headers = {},
  suppressToast = false,
  config = {},
}: ApiRequestOptions): Promise<T> {
  const token = localStorage.getItem('auth_token');
  console.log(
    'API Request:',
    `${API_URL}${url}`,
    'Token:',
    token ? 'exists' : 'missing'
  );

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getTenantHeaders(), // Add tenant header for multi-tenancy
  };

  // Add any additional headers
  if (headers && typeof headers === 'object') {
    Object.assign(requestHeaders, headers);
  }

  if (token) {
    requestHeaders.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_URL}${url}`, {
      method,
      headers: requestHeaders,
      body: data ? JSON.stringify(data) : undefined,
    });

    // Check if the response is empty or not JSON
    const contentType = response.headers.get('content-type');
    const hasContent = response.headers.get('content-length') !== '0';

    let responseData: any;

    if (
      config.skipJsonParsing ||
      !hasContent ||
      !contentType?.includes('application/json')
    ) {
      // Return an empty object for empty responses or when skipJsonParsing is true
      responseData = {};
    } else {
      // Parse JSON for normal responses
      try {
        responseData = await response.json();
      } catch (e) {
        console.error('Error parsing JSON response:', e);
        responseData = {};
      }
    }

    console.log('API Response:', url, responseData);

    if (!response.ok) {
      // Extract the error message from the complex structure
      const errorMessage = extractErrorMessage(responseData);

      // Show toast notification unless suppressed
      if (!suppressToast) {
        showErrorToast(errorMessage);
      }

      throw new Error(errorMessage);
    }

    return responseData;
  } catch (error) {
    console.error('API Error:', url, error);

    // If it's not already an Error with a message, extract one
    const errorMessage =
      error instanceof Error ? error.message : extractErrorMessage(error);

    // Show toast notification unless suppressed
    if (!suppressToast) {
      showErrorToast(errorMessage);
    }

    throw error instanceof Error ? error : new Error(errorMessage);
  }
}

export { apiClient as api };
